import {
  Button,
  Card,
  Center,
  Divider,
  Group,
  Skeleton,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCalendarMonth,
  IconChartDonut,
  IconInbox,
} from '@tabler/icons-react';
import type { RefreshInterval } from '../api/queries';
import { useCategoryBreakdown, useCategoryTitle } from '../api/queries';
import type { CategoryFilter, CategoryTotal, DateRange, OperationKind } from '../api/types';
import { categoryColor, categoryFilterOf, categoryLabel } from '../lib/categories';
import { formatMoney } from '../lib/format';

/**
 * A circle of circumference 100, so a slice is drawn by its percentage without
 * any conversion: `stroke-dasharray` takes the share as the dash, the rest as
 * the gap. The quarter subtracted from every offset starts the first slice at
 * the top instead of at three o'clock.
 */
const RADIUS = 15.915494;
const CENTRE = 21;
const QUARTER = 25;
const DONUT_SIZE = 168;

const TITLES: Record<OperationKind, string> = {
  withdrawal: 'Траты по категориям',
  deposit: 'Пополнения по категориям',
};

interface CategoryChartProps {
  /** Which direction is split up; follows the filter above the table. */
  kind: OperationKind;
  /** Inclusive dates shared by the chart and the operation list below it. */
  range: DateRange;
  /** The filter the table is under, so the slice behind it can be picked out. */
  selected: CategoryFilter | null;
  onSelect: (category: CategoryFilter | null) => void;
  onRangeChange: (range: DateRange) => void;
  refreshInterval: RefreshInterval;
}

/**
 * Where the money goes over the selected dates rather than over the page below:
 * the split comes from its own endpoint, so paging the table never moves a slice.
 *
 * Clicking a slice filters the table by it, and clicking it again clears the
 * filter — which is the only reason the chart knows about the filter at all.
 */
export function CategoryChart({
  kind,
  range,
  selected,
  onSelect,
  onRangeChange,
  refreshInterval,
}: CategoryChartProps) {
  const { data, isPending, error, refetch } = useCategoryBreakdown(
    kind,
    range,
    refreshInterval,
  );

  const categories = data?.categories ?? [];
  const thisMonth = currentMonthRange();
  const isCurrentMonth = range.since === thisMonth.since && range.until === thisMonth.until;

  return (
    <Card padding="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-end" gap="md">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon size="md" radius="md" variant="light">
              <IconChartDonut size={18} stroke={1.5} />
            </ThemeIcon>
            <div>
              <Text fw={600}>{TITLES[kind]}</Text>
              <Text size="xs" c="dimmed">
                за выбранный период, независимо от страницы ниже
              </Text>
            </div>
          </Group>

          <Group gap="xs" align="flex-end">
            <TextInput
              type="date"
              size="xs"
              w={140}
              label="С"
              aria-label="Начало периода"
              value={range.since}
              onChange={(event) => {
                const since = event.currentTarget.value;
                if (since) {
                  onRangeChange({
                    since,
                    until: since > range.until ? since : range.until,
                  });
                }
              }}
            />
            <TextInput
              type="date"
              size="xs"
              w={140}
              label="По"
              aria-label="Конец периода"
              value={range.until}
              onChange={(event) => {
                const until = event.currentTarget.value;
                if (until) {
                  onRangeChange({
                    since: until < range.since ? until : range.since,
                    until,
                  });
                }
              }}
            />
            <Button
              size="xs"
              variant="default"
              leftSection={<IconCalendarMonth size={15} stroke={1.5} />}
              disabled={isCurrentMonth}
              onClick={() => onRangeChange(currentMonthRange())}
            >
              Текущий месяц
            </Button>
          </Group>
        </Group>

        <Divider />

        {error ? (
          <ErrorState
            message={error.message}
            onRetry={() => {
              void refetch();
            }}
          />
        ) : isPending ? (
          <ChartSkeleton />
        ) : categories.length === 0 ? (
          <EmptyState />
        ) : (
          <Group align="center" gap="xl" wrap="wrap">
            <Donut
              categories={categories}
              total={data.total}
              selected={selected}
              onSelect={onSelect}
            />
            <Legend
              categories={categories}
              total={data.total}
              selected={selected}
              onSelect={onSelect}
            />
          </Group>
        )}
      </Stack>
    </Card>
  );
}

interface SliceProps {
  categories: CategoryTotal[];
  total: number;
  selected: CategoryFilter | null;
  onSelect: (category: CategoryFilter | null) => void;
}

function Donut({ categories, total, selected, onSelect }: SliceProps) {
  const titleOf = useCategoryTitle();
  let travelled = 0;

  return (
    <div style={{ position: 'relative', width: DONUT_SIZE, height: DONUT_SIZE }}>
      <svg viewBox="0 0 42 42" width={DONUT_SIZE} height={DONUT_SIZE} role="presentation">
        {categories.map((entry) => {
          const share = (entry.total / total) * 100;
          const offset = travelled;
          travelled += share;
          const filter = categoryFilterOf(entry.category);
          const faded = selected !== null && selected !== filter;

          return (
            <circle
              key={entry.category ?? 'none'}
              cx={CENTRE}
              cy={CENTRE}
              r={RADIUS}
              fill="none"
              stroke={`var(--mantine-color-${categoryColor(entry.category)}-5)`}
              strokeWidth={faded ? 4 : 5.5}
              strokeDasharray={`${share} ${100 - share}`}
              strokeDashoffset={QUARTER - offset}
              opacity={faded ? 0.3 : 1}
              style={{ cursor: 'pointer', transition: 'opacity 150ms, stroke-width 150ms' }}
              onClick={() => onSelect(selected === filter ? null : filter)}
            >
              <title>{`${categoryLabel(entry.category, titleOf(entry.category))}: ${formatMoney(
                entry.total,
              )}`}</title>
            </circle>
          );
        })}
      </svg>

      {/* Absolutely centred rather than an SVG label: the text is formatted the
          same way as everywhere else, right down to the tabular figures. */}
      <Center
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        aria-hidden="true"
      >
        <Stack gap={0} align="center">
          <Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(total)}
          </Text>
          <Text size="xs" c="dimmed">
            всего
          </Text>
        </Stack>
      </Center>
    </div>
  );
}

function Legend({ categories, total, selected, onSelect }: SliceProps) {
  return (
    <Stack gap={4} style={{ flex: '1 1 220px', minWidth: 0 }}>
      {categories.map((entry) => (
        <LegendRow
          key={entry.category ?? 'none'}
          entry={entry}
          total={total}
          selected={selected}
          onSelect={onSelect}
        />
      ))}
    </Stack>
  );
}

function LegendRow({
  entry,
  total,
  selected,
  onSelect,
}: {
  entry: CategoryTotal;
  total: number;
  selected: CategoryFilter | null;
  onSelect: (category: CategoryFilter | null) => void;
}) {
  const titleOf = useCategoryTitle();
  const filter = categoryFilterOf(entry.category);
  const picked = selected === filter;

  return (
    <UnstyledButton
      onClick={() => onSelect(picked ? null : filter)}
      aria-pressed={picked}
      px="xs"
      py={4}
      style={{
        borderRadius: 'var(--mantine-radius-sm)',
        backgroundColor: picked ? 'var(--mantine-color-dark-6)' : undefined,
      }}
    >
      <Group gap="xs" wrap="nowrap" justify="space-between">
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 3,
              flexShrink: 0,
              backgroundColor: `var(--mantine-color-${categoryColor(entry.category)}-5)`,
            }}
          />
          <Text
            size="sm"
            lineClamp={1}
            c={entry.category === null ? 'dimmed' : 'var(--mantine-color-text)'}
          >
            {categoryLabel(entry.category, titleOf(entry.category))}
          </Text>
        </Group>

        <Group gap="xs" wrap="nowrap" style={{ whiteSpace: 'nowrap' }}>
          <Text size="sm" fw={500} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(entry.total)}
          </Text>
          <Text size="xs" c="dimmed" w={40} ta="right">
            {formatShare(entry.total, total)}
          </Text>
        </Group>
      </Group>
    </UnstyledButton>
  );
}

/** Below a percent the figure would round to zero, which reads as "nothing". */
function formatShare(part: number, total: number): string {
  const share = (part / total) * 100;
  if (share < 1) {
    return '<1 %';
  }
  return `${share.toFixed(share < 10 ? 1 : 0)} %`;
}

function ChartSkeleton() {
  return (
    <Group align="center" gap="xl" wrap="wrap">
      <Skeleton circle height={DONUT_SIZE} />
      <Stack gap="xs" style={{ flex: '1 1 220px' }}>
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} height={20} radius="sm" />
        ))}
      </Stack>
    </Group>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Center py={32}>
      <Stack gap="xs" align="center" maw={320}>
        <ThemeIcon variant="light" color="red" size="lg" radius="md">
          <IconAlertTriangle size={18} stroke={1.5} />
        </ThemeIcon>
        <Text size="sm" fw={600}>
          Не удалось загрузить распределение
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          {message}
        </Text>
        <Button size="xs" variant="default" mt={4} onClick={onRetry}>
          Повторить
        </Button>
      </Stack>
    </Center>
  );
}

function EmptyState() {
  return (
    <Center py={40}>
      <Stack gap="xs" align="center" maw={280}>
        <IconInbox size={26} stroke={1.5} opacity={0.5} />
        <Text size="sm" c="dimmed" ta="center">
          Делить нечего
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          За выбранный период операций нет
        </Text>
      </Stack>
    </Center>
  );
}

/** First and last UTC calendar days of the current month, matching operation storage. */
export function currentMonthRange(now = new Date()): DateRange {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const since = formatDateInput(year, month + 1, 1);
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const until = formatDateInput(year, month + 1, lastDay);
  return { since, until };
}

function formatDateInput(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
